import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import type { Entitlement } from '@/types/Entitlement'

import DataLoader from '@/vendor/dataloader'

import type { EntitlementCheckResponse, EntitlementKey } from './types'

const api = {
  checkEntitlements: async (
    billingOrgId: string,
    entitlements: Entitlement[],
    accessToken: string,
  ): Promise<EntitlementCheckResponse> => {
    const res = await fetch(
      `${API_URL}/api/v1/entitlements/check/${billingOrgId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ entitlements }),
      },
    )

    if (!res.ok) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: res.statusText,
        cause: {
          status: res.status,
        },
      })
    }

    return (await res.json()) as EntitlementCheckResponse
  },
}

const batchEntitlements = async (
  keys: readonly EntitlementKey[],
  accessToken: string,
): Promise<(boolean | Error)[]> => {
  // Group keys by billing org
  const requestsByBillingOrg = new Map<string, Set<Entitlement>>()
  for (const key of keys) {
    if (!requestsByBillingOrg.has(key.billingOrgId)) {
      requestsByBillingOrg.set(key.billingOrgId, new Set())
    }
    requestsByBillingOrg.get(key.billingOrgId)!.add(key.entitlement)
  }

  // Execute requests for each billing org
  const billingOrgIds = Array.from(requestsByBillingOrg.keys())
  const promises = billingOrgIds.map((billingOrgId) => {
    const entitlements = Array.from(requestsByBillingOrg.get(billingOrgId)!)
    return api.checkEntitlements(billingOrgId, entitlements, accessToken)
  })

  const results = await Promise.all(promises)

  // Build result map keyed by billingOrgId::entitlement
  const resultMap = new Map<string, boolean>()
  results.forEach((response, index) => {
    const billingOrgId = billingOrgIds[index]
    for (const [entitlement, hasEntitlement] of Object.entries(
      response.entitlements,
    )) {
      const uniqueKey = `${billingOrgId}::${entitlement}`
      resultMap.set(uniqueKey, hasEntitlement)
    }
  })

  // Return results in same order as input keys
  return keys.map((key) => {
    const uniqueKey = `${key.billingOrgId}::${key.entitlement}`
    const result = resultMap.get(uniqueKey)
    return result === undefined ? false : result
  })
}

export const createEntitlementLoader = (accessToken: string) => {
  return new DataLoader<EntitlementKey, boolean, string>(
    (keys) => batchEntitlements(keys, accessToken),
    {
      cacheKeyFn: (key) => `${key.billingOrgId}::${key.entitlement}`,
    },
  )
}
